import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaUsers, 
  FaCalendarAlt, 
  FaClock, 
  FaMapMarkerAlt,
  FaCar,
  FaRoute,
  FaUserPlus,
  FaExclamationTriangle,
  FaUserCheck
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { useToast } from '../../../hooks/ToastContext';
import Header from '../../../../headerL';
import Spinner from '../../../common/Spinner';
import { CustomButton2, CustomButton8 } from '../../../Templates';
import '../../styles/GroupsPage.css';

const GroupRideDetailsPage = () => {
  const { id, rideId } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [ride, setRide] = useState(null);
  const [group, setGroup] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isUserParticipant, setIsUserParticipant] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  
  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return t('posts.not_specified');
    
    try {
      // Check if it's in HH:MM:SS format
      if (timeString.includes(':')) {
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          // Create a date object with today's date and the specified time
          const date = new Date();
          date.setHours(parseInt(parts[0], 10));
          date.setMinutes(parseInt(parts[1], 10));
          date.setSeconds(parts.length > 2 ? parseInt(parts[2], 10) : 0);
          
          return new Intl.DateTimeFormat(navigator.language || 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true // Use 12-hour format with AM/PM
          }).format(date);
        }
      }
      
      return timeString; // Return as is if the format is not as expected
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString; // Return original if there's an error
    }
  };
  
  // Load ride data
  useEffect(() => {
    const fetchRideData = async () => {
      if (!id || !rideId) {
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.missing_parameters')
        });
        navigate('/groups');
        return;
      }
      
      if (!user) {
        addToast({
          type: 'error',
          title: t('errors.authentication_required'),
          description: t('errors.login_required_action')
        });
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch ride details
        const { data: rideData, error: rideError } = await supabase
          .from('group_rides')
          .select('*, profiles(*)')
          .eq('id', rideId)
          .eq('group_id', id)
          .single();
        
        if (rideError) throw rideError;
        
        setRide(rideData);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', id)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Fetch ride participants
        const { data: participantsData, error: participantsError } = await supabase
          .from('group_ride_participants')
          .select('*, profiles(*)')
          .eq('ride_id', rideId);
        
        if (participantsError) throw participantsError;
        
        setParticipants(participantsData || []);
        
        // Check if user is a participant
        if (user) {
          const isParticipant = participantsData?.some(p => 
            p.user_id === user.id && p.status === 'confirmed'
          );
          setIsUserParticipant(isParticipant);
        }
        
      } catch (error) {
        console.error('Error fetching ride data:', error);
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: t('errors.ride_fetch_failed')
        });
        navigate(`/groups/${id}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRideData();
  }, [id, rideId, user, navigate, t, addToast]);
  
  // Join the ride
  const handleJoinRide = async () => {
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      return;
    }
    
    if (isUserParticipant) {
      addToast({
        type: 'info',
        title: t('info'),
        description: t('groups.already_joined')
      });
      return;
    }
    
    setIsJoining(true);
    
    try {
      // Check if there are available seats
      if (ride.available_seats < 1) {
        addToast({
          type: 'error',
          title: t('errors.join_error'),
          description: t('groups.no_available_seats')
        });
        setIsJoining(false);
        return;
      }
      
      // Join the ride
      const { data: joinData, error: joinError } = await supabase
        .from('group_ride_participants')
        .insert({
          ride_id: rideId,
          user_id: user.id,
          role: 'passenger',
          status: 'confirmed'
        })
        .select('*, profiles(*)')
        .single();
      
      if (joinError) throw joinError;
      
      // Update available seats
      const { error: updateError } = await supabase
        .from('group_rides')
        .update({ available_seats: ride.available_seats - 1 })
        .eq('id', rideId);
      
      if (updateError) throw updateError;
      
      // Update UI
      setRide({
        ...ride,
        available_seats: ride.available_seats - 1
      });
      setParticipants([...participants, joinData]);
      setIsUserParticipant(true);
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.joined_ride_success')
      });
      
    } catch (error) {
      console.error('Error joining ride:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.join_ride_failed')
      });
    } finally {
      setIsJoining(false);
    }
  };
  
  // Leave the ride
  const handleLeaveRide = async () => {
    if (!user || !isUserParticipant) return;
    
    try {
      // Find the user's participant record
      const userParticipant = participants.find(p => p.user_id === user.id);
      
      if (!userParticipant) {
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.not_a_participant')
        });
        return;
      }
      
      // Driver cannot leave their own ride
      if (userParticipant.role === 'driver') {
        addToast({
          type: 'error',
          title: t('errors.leave_error'),
          description: t('groups.driver_cannot_leave')
        });
        return;
      }
      
      // Leave the ride
      const { error: leaveError } = await supabase
        .from('group_ride_participants')
        .delete()
        .eq('id', userParticipant.id);
      
      if (leaveError) throw leaveError;
      
      // Update available seats
      const { error: updateError } = await supabase
        .from('group_rides')
        .update({ available_seats: ride.available_seats + 1 })
        .eq('id', rideId);
      
      if (updateError) throw updateError;
      
      // Update UI
      setRide({
        ...ride,
        available_seats: ride.available_seats + 1
      });
      setParticipants(participants.filter(p => p.id !== userParticipant.id));
      setIsUserParticipant(false);
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.left_ride_success')
      });
      
    } catch (error) {
      console.error('Error leaving ride:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.leave_ride_failed')
      });
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return t('posts.not_specified');
      }
      return new Intl.DateTimeFormat(navigator.language || 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('posts.not_specified');
    }
  };
  
  // Navigate back to group
  const handleBack = () => {
    navigate(`/groups/${id}`);
  };
  
  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }
  
  if (!ride) {
    return (
      <>
        <div className="groups-page">
          <div className="groups-empty">
            <h3>{t('groups.ride_not_found')}</h3>
            <CustomButton2
              label={t('groups.back_to_group')}
              onClick={handleBack}
            />
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <div className="group-details-page">
        <div className="back-button" style={{ marginBottom: '20px' }}>
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <div className="ride-details-header">
          <h1 className="group-details-title">{ride.title}</h1>
          <div className="group-context">
            {t('groups.in_group')}: <strong>{group?.name}</strong>
          </div>
          
          <div className="ride-meta">
            <div className="ride-meta-item">
              <FaCalendarAlt className="icon" />
              <span>{formatDate(ride.travel_date)}</span>
            </div>
            <div className="ride-meta-item">
              <FaClock className="icon" />
              <span>{formatTime(ride.travel_time)}</span>
            </div>
            <div className="ride-meta-item">
              <FaRoute className="icon" />
              <span>{ride.distance_km} km</span>
            </div>
            <div className="ride-meta-item">
              <FaUsers className="icon" />
              <span>
                {ride.max_seats - ride.available_seats}/{ride.max_seats} {t('groups.seats_taken')}
              </span>
            </div>
          </div>
        </div>
        
        <div className="group-details-content">
          <div className="group-details-main">
            {ride.description && (
              <div className="group-description">
                <h3>{t('groups.description')}</h3>
                <p>{ride.description}</p>
              </div>
            )}
            
            <div className="ride-route-details">
              <h3>{t('groups.route_details')}</h3>
              
              <div className="ride-addresses">
                <div className="ride-address">
                  <FaMapMarkerAlt />
                  <div>
                    <strong>{t('groups.from')}:</strong>
                    <p>{ride.address_from_street}</p>
                    {ride.address_from_city && (
                      <p className="address-details">{ride.address_from_city}, {ride.address_from_country}</p>
                    )}
                  </div>
                </div>
                
                <div className="ride-address">
                  <FaMapMarkerAlt />
                  <div>
                    <strong>{t('groups.to')}:</strong>
                    <p>{ride.address_to_street}</p>
                    {ride.address_to_city && (
                      <p className="address-details">{ride.address_to_city}, {ride.address_to_country}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {ride.map_image_url ? (
                <div className="ride-map">
                  <img 
                    src={ride.map_image_url} 
                    alt={t('groups.route_map')}
                    className="route-map-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-map.jpg';
                      console.error('Error loading route map image');
                    }}
                  />
                </div>
              ) : (
                <div className="ride-map">
                  <img
                    src="/placeholder-map.jpg"
                    alt={t('groups.route_map')}
                    className="route-map-image"
                  />
                </div>
              )}
            </div>
            
            <div className="ride-participants">
              <h3>{t('groups.participants')} ({participants.length})</h3>
              
              {participants.length === 0 ? (
                <div className="empty-list-message">
                  <FaUsers className="empty-icon" />
                  <p>{t('groups.no_participants_yet')}</p>
                </div>
              ) : (
                <div className="participants-list">
                  {participants.map(participant => (
                    <div key={participant.id} className="group-member">
                      <div 
                        className="group-member-avatar"
                        style={{
                          backgroundImage: participant.profiles?.avatar_url 
                            ? `url(${participant.profiles.avatar_url})` 
                            : 'none'
                        }}
                      />
                      <div className="group-member-info">
                        <div className="group-member-name">
                          {participant.profiles?.username || t('groups.unknown_user')}
                        </div>
                        <div className="group-member-role">
                          {participant.role === 'driver' ? (
                            <><FaCar /> {t('groups.driver')}</>
                          ) : (
                            <><FaUserCheck /> {t('groups.passenger')}</>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="group-details-sidebar">
            <div className="group-sidebar-section">
              <h3 className="group-sidebar-title">{t('groups.driver')}</h3>
              <div className="driver-info">
                <div 
                  className="driver-avatar"
                  style={{
                    backgroundImage: ride.profiles?.avatar_url 
                      ? `url(${ride.profiles.avatar_url})` 
                      : 'none'
                  }}
                />
                <div className="driver-details">
                  <div className="driver-name">
                    {ride.profiles?.username || t('groups.unknown_user')}
                  </div>
                  <div className="car-details">
                    <FaCar />
                    <span>{ride.car || t('groups.car_not_specified')}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="group-sidebar-section">
              <h3 className="group-sidebar-title">{t('groups.ride_actions')}</h3>
              
              {!isUserParticipant ? (
                <CustomButton2
                  label={t('groups.join_ride')}
                  onClick={handleJoinRide}
                  disabled={isJoining || ride.available_seats < 1}
                  icon={isJoining ? <Spinner size="small" showText={false} /> : <FaUserPlus />}
                  fullWidth
                />
              ) : (
                <CustomButton2
                  label={t('groups.leave_ride')}
                  onClick={handleLeaveRide}
                  fullWidth
                />
              )}
              
              {ride.available_seats < 1 && !isUserParticipant && (
                <div className="no-seats-warning">
                  <FaExclamationTriangle />
                  <div>
                    <strong>{t('groups.no_available_seats')}</strong>
                    <p>{t('groups.ride_is_full')}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="group-sidebar-section">
              <h3 className="group-sidebar-title">{t('groups.ride_type')}</h3>
              <div className="ride-type">
                {ride.travel_type === 'recurring' ? (
                  <>
                    <FaCalendarAlt />
                    <span>{t('groups.recurring_ride')}</span>
                  </>
                ) : (
                  <>
                    <FaCalendarAlt />
                    <span>{t('groups.one_time_ride')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupRideDetailsPage;